<template>
  <v-form
    :id='`edit-guest-form-${guest.id}`'
    @submit.prevent='submitGuest'
  >
    <confirm ref="confirm"/>

    <v-row v-if="isRequired('typology')">
      <v-col cols="12" md="6">
        <v-select
          v-model='typology'
          :items="roomChiefTypologies"
          :label='labelFor("typology_friendly", {validation_field: "typology"})'
          :prepend-icon="this.isSingle ? 'mdi-account' : 'mdi-account-group'"
          :error-messages='typologyErrors'
          @input="handleTypologyChange()"
          @blur="setDirtiness('typology')"
          v-show="bookedGuestsCount > 1"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model='first_name'
          :label='labelFor("first_name")'
          prepend-icon='mdi-account-outline'
          :error-messages='firstNameErrors'
          @input="setDirtiness('first_name')"
          @blur="setDirtiness('first_name')"
          v-if="isRequired('first_name')"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model='last_name'
          :label='labelFor("last_name")'
          prepend-icon='mdi-account'
          :error-messages='lastNameErrors'
          @input="setDirtiness('last_name')"
          @blur="setDirtiness('last_name')"
          v-if="isRequired('last_name')"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="6">
        <v-radio-group
          v-model='sex'
          row
          :error-messages="sexErrors"
          :prepend-icon="!!sex ? (sex == 'M' ? 'mdi-gender-male' : 'mdi-gender-female') : 'mdi-gender-male-female'"
          @change="setDirtiness('sex')"
          @blur="setDirtiness('sex')"
          v-if="isRequired('sex')"
        >
          <!--span class='mr-4'>{{ $t('guest.attributes.sex') }}</span-->
          <v-radio
            :label='$t("guest.attributes.genders.male")'
            value='M'
          />
          <v-radio
            :label='$t("guest.attributes.genders.female")'
            value='F'
          />
        </v-radio-group>
      </v-col>
      <v-col cols="12" md="6">
        <v-menu
          ref="menu_birthdate"
          v-model="birthdatePickerOpened"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="birthdate"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator='{ on }'>
            <v-text-field
              :value='formattedBirthdate'
              :label='labelFor("birthdate")'
              prepend-icon='mdi-calendar'
              :error-messages="birthdateErrors"
              readonly
              v-on='on'
            />
          </template>
          <v-date-picker
            scrollable
            ref="birthday_picker"
            v-model='birthdate'
            first-day-of-week='1'
            :locale='$i18n.locale'
            :max="new Date().toISOString().substr(0, 10)"
            min="1900-01-01"
            @input="setDirtiness('birthdate')"
            @blur="setDirtiness('birthdate')"
            @click:date="$refs.menu_birthdate.save(birthdate)"
          />
        </v-menu>
      </v-col>
    </v-row>

    <v-row v-if="isRequired('country_of_birth_id')">
      <v-col cols="12" sm="6">
        <v-autocomplete
          v-model='country_of_birth_id'
          :items='birthCountries'
          item-text='name'
          item-value='id'
          :error-messages="countryOfBirthIdErrors"
          :label='labelFor("country_of_birth")'
          prepend-icon='mdi-earth'
          :search-input.sync="country_of_birth_name"
          :loading="isLoading.birthCountries"
          hide-no-data
          @input="setDirtiness('country_of_birth_id')"
          @blur="setDirtiness('country_of_birth_id')"
          @change="clearFields(['place_of_birth_id'])"
          persistent-hint
          :hint="this.$t('hints.search_autocomplete')"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-autocomplete
          v-model='place_of_birth_id'
          v-if="country_of_birth_id"
          :items='birthPlaces'
          item-text='name'
          item-value='id'
          :label='labelFor("place_of_birth", {validation_field: "place_of_birth_id", optional: true})'
          prepend-icon='mdi-city'
          :search-input.sync="place_of_birth_name"
          :error-messages='placeOfBirthIdErrors'
          :loading="isLoading.birthPlaces"
          hide-no-data
          @input="setDirtiness('place_of_birth_id')"
          @blur="setDirtiness('place_of_birth_id')"
          persistent-hint
          :hint="country_of_birth_id && this.$t('hints.search_autocomplete')"
        ></v-autocomplete>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-checkbox
          v-model="countryOfResidenceAsBirth"
          :label="$t('guest.residence_as_birth')"
          @change="handleCountryOfResidenceAsBirth"
        ></v-checkbox>
      </v-col>
    </v-row>

    <transition name='slide-fade'>
      <v-row v-if="isRequired('country_of_residence_id')">
        <v-col cols="12" sm="6">
          <v-autocomplete
            v-model='country_of_residence_id'
            :items='residenceCountries'
            item-text='name'
            item-value='id'
            :error-messages="countryOfResidenceIdErrors"
            :label='labelFor("country_of_residence", {validation_field: "country_of_residence_id"})'
            prepend-icon='mdi-earth'
            :search-input.sync="country_of_residence_name"
            :loading="isLoading.residenceCountries"
            hide-no-data
            @input="setDirtiness('country_of_residence_id')"
            @blur="setDirtiness('country_of_residence_id')"
            @change="clearFields(['city_of_residence_id'])"
            persistent-hint
            :hint="this.$t('hints.search_autocomplete')"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-autocomplete
            v-model='city_of_residence_id'
            v-if="country_of_residence_id"
            :items='residenceCities'
            item-text='name'
            item-value='id'
            :label='labelFor("city_of_residence", {validation_field: "city_of_residence_id", optional: true})'
            prepend-icon='mdi-city'
            :search-input.sync="city_of_residence_name"
            :error-messages='cityOfResidenceIdErrors'
            @input="setDirtiness('city_of_residence_id')"
            @blur="setDirtiness('city_of_residence_id')"
            :loading="isLoading.birthPlaces"
            hide-no-data
            persistent-hint
            :hint="country_of_birth_id && this.$t('guest.hints.city_of_residence')"
          ></v-autocomplete>
        </v-col>
      </v-row>
    </transition>

    <v-row v-if="isRequired('address')">
      <v-col cols='12' sm='6'>
        <v-text-field
          v-model='address'
          :label='labelFor("address")'
          :error-messages='addressErrors'
          @input='$v.address.$touch()'
          @blur='$v.address.$touch()'
          prepend-icon='mdi-road'
          persistent-hint
          :hint="this.$t('guest.hints.address')"
        />
      </v-col>

      <v-col cols='12' sm='6'>
        <v-text-field
          v-model='zip'
          :label='labelFor("zip")'
          :error-messages='zipErrors'
          @input='$v.zip.$touch()'
          @blur='$v.zip.$touch()'
          prepend-icon='mdi-mailbox-outline'
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-checkbox
          v-model="countryOfCitizenshipAsBirth"
          :label="$t('guest.citizenship_as_birth')"
          @change="handleCountryOfCitizenshipAsBirth"
        ></v-checkbox>
      </v-col>
    </v-row>

    <transition name='slide-fade'>
      <v-row v-if="isRequired('citizenship_id')">
        <v-col cols="12" sm="6">
          <v-autocomplete
            v-model='citizenship_id'
            :items='citizenshipCountries'
            item-text='name'
            item-value='id'
            :error-messages="citizenshipIdErrors"
            :label='labelFor("citizenship", {validation_field: "citizenship_id"})'
            prepend-icon='mdi-earth'
            :search-input.sync="citizenship_name"
            :loading="isLoading.citizenshipCountries"
            hide-no-data
            @input="setDirtiness('citizenship_id')"
            @blur="setDirtiness('citizenship_id')"
            @change="handleDocumentsCountriesChanged"
            persistent-hint
            :hint="this.$t('hints.search_autocomplete')"
          />
        </v-col>
      </v-row>
    </transition>

    <v-row v-if="isRequired('fiscal_code')">
      <v-col cols="12" md="6">
        <v-text-field
          v-model='fiscal_code'
          :label='labelFor("fiscal_code")'
          :error-messages='fiscalCodeErrors'
          @input="setDirtiness('fiscal_code')"
          @blur="setDirtiness('fiscal_code')"
          prepend-icon='mdi-account-badge'
          persistent-hint
          :hint="$t('guest.hints.fiscal_code')"
        />
      </v-col>
    </v-row>

    <!--v-row v-if="guestsRecognition && !isRoomChiefOrSingle">
      <v-col class="d-flex">
        <v-checkbox
          v-model="useRoomChiefContacts"
          :label="$t('guest.use_contacts_of_the_chief_room')"
          @change="handleUseRoomChiefContacts"
        ></v-checkbox>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-icon small
                    class="ml-2"
                    @click.stop
                    v-on="on">mdi-information-outline</v-icon>
          </template>
          {{ $t('guest.hints.use_room_chief_contacts') }}
        </v-tooltip>
      </v-col>
    </v-row-->

    <transition-group name='slide-fade'>
      <v-row key="email-section">
        <v-col cols="12" sm="6">
          <v-text-field
            required
            v-model='email'
            :label='labelFor("email")'
            prepend-icon='mdi-email-outline'
            :error-messages='emailErrors'
            @input="setDirtiness('email')"
            @blur="setDirtiness('email')"
            v-if="isRequired('email')"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field
            required
            v-model='email_confirmation'
            :label='labelFor("email_confirmation")'
            prepend-icon='mdi-email-outline'
            :error-messages='emailConfirmationErrors'
            @input="setDirtiness('email_confirmation')"
            @blur="setDirtiness('email_confirmation')"
            v-if="isRequired('email_confirmation')"
          />
        </v-col>
      </v-row>

      <v-row v-if="isRequired('phone')" key="phone-section">
        <v-col cols="12" sm="6">
          <v-autocomplete
            v-model='phone_prefix'
            :items='phonePrefixes'
            item-text='label'
            item-value='phone_prefix'
            :label='labelFor("phone_prefix")'
            prepend-icon='mdi-earth'
            persistent-hint
            :hint='$t("guest.hints.phone_prefix")'
            :search-input.sync="phonePrefixSearch"
            :loading="isLoading.phonePrefixes"
            :error-messages="phonePrefixErrors"
            hide-no-data
            @change="setDirtiness('phone_prefix')"
            @input="setDirtiness('phone_prefix')"
          >
          </v-autocomplete>
        </v-col>

        <v-col cols="12" sm="6">
          <v-text-field
            required
            v-model='phone'
            :label='labelFor("phone")'
            prepend-icon='mdi-phone'
            persistent-hint
            :hint="$t(`${contractCheckinStepPresent ? 'guest' : 'profile'}.hints.phone`)"
            :error-messages="phoneErrors"
            @input="setDirtiness('phone')"
            @blur="setDirtiness('phone')"
          />
        </v-col>
      </v-row>
    </transition-group>

    <v-row v-if="isRoomChief">
      <v-col>
        <v-checkbox
          v-model="documentIssuedCountryAsResidence"
          :label="$t('guest.document_issued_as_residence')"
          @change="handleDocumentIssuedCountryAsResidence"
        ></v-checkbox>
      </v-col>
    </v-row>

    <v-row v-if="isRequired('document_issued_country_id')">
      <v-col cols="12" sm="6">
        <v-autocomplete
          v-model='document_issued_country_id'
          :items='documentCountries'
          item-text='name'
          item-value='id'
          :error-messages="documentIssuedCountryIdIdErrors"
          :label='labelFor("document_issued_country")'
          prepend-icon='mdi-earth'
          :search-input.sync="document_issued_country_name"
          :loading="isLoading.documentCountries"
          hide-no-data
          @input="setDirtiness('document_issued_country_id')"
          @blur="setDirtiness('document_issued_country_id')"
          @change="clearFields(['document_issued_location_id'])"
          persistent-hint
          :hint="this.$t('hints.search_autocomplete')"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <transition name='slide-fade'>
          <v-autocomplete
            v-if="document_issued_country_id && isRequired('document_issued_location_id')"
            v-model='document_issued_location_id'
            :items="documentIssuedLocationCities"
            item-text='name'
            item-value='id'
            :label='labelFor("document_issued_location", {validation_field: "document_issued_location_id"})'
            prepend-icon='mdi-city'
            :error-messages='documentIssuedLocationIdErrors'
            :search-input.sync="document_issued_location_name"
            :loading="isLoading.documentIssuedLocationCities"
            hide-no-data
            @input="setDirtiness('document_issued_location_id')"
            @blur="setDirtiness('document_issued_location_id')"
          />
        </transition>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" sm="6">
        <v-select
          v-model='identification_document_id'
          :items="identificationDocuments"
          item-value="id"
          item-text="name"
          :label='labelFor("document_type", {validation_field: "identification_document_id"})'
          prepend-icon='mdi-account-card-details-outline'
          :error-messages='identificationDocumentIdErrors'
          :no-data-text="$t('guest.hints.identification_document_no_data')"
          @change="setDirtiness('identification_document_id')"
          @blur="setDirtiness('identification_document_id')"
          v-if="isRequired('identification_document_id')"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          required
          v-model='document_number'
          :label='labelFor("document_number")'
          prepend-icon='mdi-counter'
          :error-messages='documentNumberErrors'
          @input="setDirtiness('document_number')"
          @blur="setDirtiness('document_number')"
          v-if="isRequired('document_number')"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col v-if="isRequired('document_issued_on')" cols='12' sm='6'>
        <v-menu
          ref="menu_document_issued_on"
          v-model="documentIssuedOnPickerOpened"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="document_issued_on"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator='{ on }'>
            <v-text-field
              :value='formattedDate(document_issued_on)'
              :label='labelFor("document_issued_on")'
              prepend-icon='mdi-calendar'
              :error-messages="documentIssuedOnErrors"
              readonly
              v-on='on'
            />
          </template>
          <v-date-picker
            scrollable
            ref="document_issued_on_picker"
            v-model='document_issued_on'
            first-day-of-week='1'
            :locale='$i18n.locale'
            :max="new Date().toISOString().substr(0, 10)"
            min="1900-01-01"
            @input="setDirtiness('document_issued_on')"
            @blur="setDirtiness('document_issued_on')"
            @click:date="$refs.menu_document_issued_on.save(document_issued_on)"
          />
        </v-menu>
      </v-col>
      <v-col v-if="isRequired('document_expiring_date')" cols='12' sm='6'>
        <v-menu
          ref="menu_document_expiring_date"
          v-model="documentExpiringDatePickerOpened"
          :close-on-content-click="false"
          :nudge-right="40"
          :return-value.sync="document_expiring_date"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator='{ on }'>
            <v-text-field
              :value='formattedDate(document_expiring_date)'
              :label='labelFor("document_expiring_date")'
              prepend-icon='mdi-calendar'
              :error-messages="documentExpiringDateErrors"
              readonly
              v-on='on'
            />
          </template>
          <v-date-picker
            scrollable
            ref="document_expiring_date_picker"
            v-model='document_expiring_date'
            first-day-of-week='1'
            :locale='$i18n.locale'
            :min="new Date().toISOString().substr(0, 10)"
            :max="getMeADate(15).toISOString().substr(0, 10)"
            @input="setDirtiness('document_expiring_date')"
            @blur="setDirtiness('document_expiring_date')"
            @click:date="$refs.menu_document_expiring_date.save(document_expiring_date)"
          />
        </v-menu>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { utilityMixin } from '../../mixins/utility_mixin'
import { guestMixin } from '../../mixins/guest_mixin'
import { currentUserMixin } from '../../mixins/current_user_mixin'
import { accountDataMixin } from '../../mixins/account_data_mixin'
import { undoManagementMixin } from '../../mixins/undo_management_mixin'
import { required, requiredIf, email, sameAs } from 'vuelidate/lib/validators'
import { mapState, mapGetters } from 'vuex'
import Confirm from "../base/Confirm"

const FORM_FIELDS = [
  'first_name',
  'last_name',
  'sex',
  'birthdate',
  'country_of_birth_id',
  'place_of_birth_id',
  'country_of_residence_id',
  'city_of_residence_id',
  'citizenship_id',
  'email',
  'email_confirmation',
  'phone_prefix',
  'phone',
  'document_issued_country_id',
  'identification_document_id',
  'document_number',
  'document_issued_location_id',
  'document_issued_on',
  'document_expiring_date',
  'typology',
  'fiscal_code',
  'address',
  'zip'
]

export default {
  name: 'GuestForm',

  props: {
    order: {
      type: Object,
      required: true,
    },
    guest: {
      type: Object,
      required: true,
    },
  },

  mixins: [
    validationMixin,
    guestMixin,
    currentUserMixin,
    accountDataMixin,
    undoManagementMixin,
    utilityMixin,
  ],

  components: {
    Confirm,
  },

  validations() {
    const fiscalCodeRequired = (this.isItalianCitizen) && (this.isRoomChiefOrSingle)
    const zipRequired = this.isResidentInItaly && (this.isRoomChiefOrSingle)
    return {
      first_name:               { required },
      last_name:                { required },
      birthdate:                { required },
      sex:                      { required },
      typology:                 { required: requiredIf(_ => { return this.isRoomChiefOrSingle }) },
      country_of_birth_id:      { required },
      place_of_birth_id:        { required: requiredIf(_ => { return this.isBirthInItaly }) },
      country_of_residence_id:  { required: requiredIf(_ => { return !this.countryOfResidenceAsBirth }) },
      city_of_residence_id:     { required: requiredIf(_ => { return this.isResidentInItaly && !this.countryOfResidenceAsBirth }) },
      address:                  { required: requiredIf(_ => { return (this.isRoomChiefOrSingle) }) },
      zip:                      {
        required: requiredIf(_ => { return (this.isRoomChiefOrSingle) }),
        zipValidator(value) {
          if (!zipRequired) return true
          return /^\d{5}$/i.test(value)       // zip validation
        },
      },
      citizenship_id:           { required: requiredIf(_ => { return !this.countryOfCitizenshipAsBirth }) },
      email:                    { required: requiredIf(_ => { return this.isRoomChiefOrSingle }), email },
      email_confirmation:       { required: requiredIf(_ => { return this.isRoomChiefOrSingle }), email, sameAsEmail: sameAs('email') },
      phone_prefix:             { required: requiredIf(_ => { return this.isRoomChiefOrSingle }) },
      phone:                    { required: requiredIf(_ => { return this.isRoomChiefOrSingle }) },
      document_issued_country_id:   { required: requiredIf(_ => { return !this.documentIssuedCountryAsResidence && (this.isRoomChiefOrSingle) }) },
      document_issued_location_id:  { required: requiredIf(_ => { return !this.documentIssuedCountryAsResidence && (this.isRoomChiefOrSingle) && (this.isItalianCitizen || this.isDocumentIssuedInItaly) }) },
      identification_document_id:   { required: requiredIf(_ => { return this.isRoomChiefOrSingle }) },
      document_number:              { required: requiredIf(_ => { return this.isRoomChiefOrSingle }) },
      document_issued_on:           { required: requiredIf(_ => { return this.isRoomChiefOrSingle }) },
      document_expiring_date:       { required: requiredIf(_ => { return this.isRoomChiefOrSingle }) },
      fiscal_code: {
        required: requiredIf(_ => { return fiscalCodeRequired }),
        fiscalCodeValidator(value) {
          if (!fiscalCodeRequired) return true
          return /^[A-Z]{6}[0-9]{2}[A-Z]{1}[0-9]{2}[A-Z]{1}[0-9]{3}[A-Z]{1}$/i.test(value)       // fiscal code validation
        },
      },
    }
  },

  data() {
    return {
      ...this.guest,
      country_of_birth_id:          this.guest.country_of_birth?.id,
      country_of_birth_name:        this.guest.country_of_birth?.name,
      place_of_birth_id:            this.guest.place_of_birth?.id,
      place_of_birth_name:          this.guest.place_of_birth?.name,
      country_of_residence_id:      this.guest.country_of_residence?.id,
      country_of_residence_name:    this.guest.country_of_residence?.name,
      city_of_residence_id:         this.guest.city_of_residence?.id,
      city_of_residence_name:       this.guest.city_of_residence?.name,
      citizenship_id:               this.guest.citizenship?.id,
      citizenship_name:             this.guest.citizenship?.name,
      email_confirmation:           this.guest.email,
      document_issued_country_id:   this.guest.document_issued_country?.id,
      document_issued_country_name: this.guest.document_issued_country?.name,
      identification_document_id:   this.guest.identification_document?.id,
      document_issued_location_id:  this.guest.document_issued_location?.id,
      document_issued_location_name:this.guest.document_issued_location?.name,
      document_issued_on:           this.guest.document_issued_on,
      document_expiring_date:       this.guest.document_expiring_date,
      phonePrefixSearch:            this.guest.phone_prefix,
      birthCountries: [],
      birthPlaces: [],
      residenceCountries: [],
      documentCountries: [],
      residenceCities: [],
      documentIssuedLocationCities: [],
      citizenshipCountries: [],
      birthdatePickerOpened: false,
      documentIssuedOnPickerOpened: false,
      documentExpiringDatePickerOpened: false,
      serverSideErrors: {},
      initialValues: {},
      countryOfResidenceAsBirth: null,
      countryOfCitizenshipAsBirth: null,
      documentIssuedCountryAsResidence: null,
      useRoomChiefContacts: null,
      isLoading: {
        birthCountries: false,
        birthPlaces: false,
        residenceCountries: false,
        residenceCities: false,
        citizenshipCountries: false,
        documentCountries: false,
        documentIssuedLocationCities: false,
        phonePrefixes: false,
      },
      isComplete: false,
    }
  },

  async created() {
    // User must choose a typology only for chief and when these are more than one
    if (this.guest.new && this.isRoomChief && this.roomChiefTypologies.length > 1) {
      this.typology = null
    }
    if (this.guest.new && this.isRoomChiefOrSingle && !this.isCurrentUserLegalPerson) {
      let user = this.orderCustomer || this.currentUser;
      this.copyDataFromCurrentCustomer(user)
    }
    this.countryOfResidenceAsBirth = !this.guest.new &&
                                      this.country_of_residence_id === this.country_of_birth_id &&
                                      this.city_of_residence_id === this.place_of_birth_id
    this.countryOfCitizenshipAsBirth = !this.guest.new &&
                                        this.citizenship_id === this.country_of_birth_id
    this.documentIssuedCountryAsResidence = !this.guest.new &&
      this.document_issued_country_id === this.country_of_residence_id &&
      this.document_issued_location_id === this.city_of_residence_id
    if (!this.guest.new) {
      this.useRoomChiefContacts = this.email == null &&
                                  this.phone_prefix == null &&
                                  this.phone == null
    }
    this.initialValues = this.getCurrentValues()
    this.checkCompletion()
    if (this.$v.$invalid) {
      this.$emit('form:invalid', true)
    }
  },

  computed: {
    ...mapState('layout', ['submitting', 'completedCheckinSteps']),
    ...mapState('mappings', [
      'countries',
      'cities',
      'identificationDocuments',
      'phonePrefixes',
    ]),
    ...mapGetters('profile', [ 'currentUser' ]),
    isBirthInItaly() {
      return this.country_of_birth_id == this.countryIds.italy
    },
    isResidentInItaly() {
      return this.country_of_residence_id == this.countryIds.italy
    },
    isItalianCitizen() {
      return this.citizenship_id == this.countryIds.italy
    },
    isDocumentIssuedInItaly() {
      return this.document_issued_country_id == this.countryIds.italy
    },
    // isExtraUeCitizenship() {
    //   const citizenship = this.citizenshipCountries.find(citizenship => citizenship.id == this.citizenship_id)
    //   return !!citizenship && !citizenship.ue
    // },
    formattedBirthdate() {
      return this.formattedDate(this.birthdate)
    },
    identificationDocumentIdErrors() {
      const errors = []
      if (!this.$v.identification_document_id.$dirty) return errors
      !!this.serverSideErrors.identification_document_id &&
        errors.push(
          this.serverSideErrors.identification_document_id.join(' - ')
        )
      !this.$v.identification_document_id.required && errors.push(this.$t('errors.required'))
      return errors
    },
    documentIssuedLocationIdErrors() {
      const errors = []
      if (!this.$v.document_issued_location_id.$dirty) return errors
      !!this.serverSideErrors.document_issued_location_id && errors.push(this.serverSideErrors.document_issued_location_id.join(' - '))
      !this.$v.document_issued_location_id.required && errors.push(this.$t('errors.required'))
      return errors
    },
    countryOfBirthIdErrors() {
      const errors = []
      if (!this.$v.country_of_birth_id.$dirty) return errors
      !!this.serverSideErrors.country_of_birth_id && errors.push(this.serverSideErrors.country_of_birth_id.join(' - '))
      !this.$v.country_of_birth_id.required && errors.push(this.$t('errors.required'))
      return errors
    },
    placeOfBirthIdErrors() {
      const errors = []
      if (!this.$v.place_of_birth_id.$dirty) return errors
      !!this.serverSideErrors.place_of_birth_id && errors.push(this.serverSideErrors.place_of_birth_id.join(' - '))
      !this.$v.place_of_birth_id.required && errors.push(this.$t('errors.required'))
      return errors
    },
    countryOfResidenceIdErrors() {
      const errors = []
      if (!this.$v.country_of_residence_id.$dirty) return errors
      !!this.serverSideErrors.country_of_residence_id && errors.push(this.serverSideErrors.country_of_residence_id.join(' - '))
      !this.$v.country_of_residence_id.required && errors.push(this.$t('errors.required'))
      return errors
    },
    cityOfResidenceIdErrors() {
      const errors = []
      if (!this.$v.city_of_residence_id.$dirty) return errors
      !!this.serverSideErrors.city_of_residence_id && errors.push(this.serverSideErrors.city_of_residence_id.join(' - '))
      !this.$v.city_of_residence_id.required &&
      errors.push(this.$t('errors.required'))
      return errors
    },
    citizenshipIdErrors() {
      const errors = []
      if (!this.$v.citizenship_id.$dirty) return errors
      !!this.serverSideErrors.citizenship_id && errors.push(this.serverSideErrors.citizenship_id.join(' - '))
      !this.$v.citizenship_id.required && errors.push(this.$t('errors.required'))
      return errors
    },
    reservation() {
      return this.order.reservations.find(
        reservation => reservation.id == this.guest.reservation_id
      )
    },
    contractCheckinStepPresent() {
      return this.completedCheckinSteps.filter(x => x.name == 'contract')[0]
    },
    guestsRecognition() {
      return this.reservation?.guests_recognition
    },
    guestUpdatable() {
      return this.guestsRecognition && !['signing', 'signed', 'sent'].includes(this.reservation.customer_contract_state)
    },
    documentIssuedCountryIdIdErrors() {
      const errors = []
      if (!this.$v.document_issued_country_id.$dirty) return errors
      !!this.serverSideErrors.document_issued_country_id && errors.push(this.serverSideErrors.document_issued_country_id.join(' - '))
      !this.$v.document_issued_country_id.required && errors.push(this.$t('errors.required'))
      return errors
    }
  },

  methods: {
    labelsPrefix() {
      return 'guest.attributes'
    },
    checkCompletion() {
      this.$v.$touch()
      if (!this.$v.$anyError) {
        this.isComplete = true
        this.$emit('form:completed', {
          guestId: this.guest.id,
        })
      }
      this.$v.$reset()
    },
    submitGuest() {
      this.serverSideErrors = {}
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$emit('form:submittedWithError', { guestId: this.guest.id })
        return
      }

      this.$store.dispatch('layout/clearAlerts')

      if (this.guestsRecognition && !this.guest.new && !this.guestUpdatable) {
        this.$refs.confirm.open(this.$t('commons.confirm'),
          this.$t('guest.alerts.confirm_start_identification_after_changes', { name: this.guest.first_name }),
            { color: 'warning', yesNo: true }
        ).then((confirm) => {
            confirm ? this.createOrUpdateGuest() : this.undoChanges()
        })
        // if (confirm("L'identificazione delle persone utilizza questi dati e la modifica implica ricominciare il processo, sei sicuro?")) {
        //   this.createOrUpdateGuest()
        // } else {
        //   this.undoChanges()
        // }
      } else {
        this.createOrUpdateGuest()
      }
    },
    createOrUpdateGuest() {
      //console.log(`createOrUpdateGuest`)
      this.forceDataDueToExceptionalConditions()

      const action = this.guest.new ? 'order/createGuest' : 'order/updateGuest'
      this.$store.dispatch(action, {
          order: this.order,
          guest: {
            id: this.guest.id,
            reservation_id: this.guest.reservation_id,
            ...this.getCurrentValues(),
          },
        })
        .then(guest => {
          // console.log(` createOrUpdateGuest ${action} guest `, guest.id)
          this.initialValues = this.getCurrentValues()
          this.$v.$reset()
          this.$emit('form:submitted', { guestId: guest.id, newGuest: this.guest.new })
        }, error => {
          //console.log(` createOrUpdateGuest ${action} err `, error)
          if (!!error && ![500, 401, 502, 503, 504].includes(error.status)) {
            this.$store.dispatch('layout/replaceAlert', {
              type: 'error',
              key: 'generic_submit_error',
            })
          }
          this.serverSideErrors = error?.body?.errors || {}
          this.$emit('form:submittedWithError', { guestId: this.guest.id })
        })
    },
    forceDataDueToExceptionalConditions() {
      // console.log(` - before forcing data place_of_birth_id:${this.place_of_birth_id} city_of_residence_id:${this.city_of_residence_id}`)
      // If checked copy data from the relative field
      if (this.countryOfResidenceAsBirth) {
        this.country_of_residence_id = this.country_of_birth_id
        this.city_of_residence_id = this.place_of_birth_id
      }
      if (this.countryOfCitizenshipAsBirth) {
        this.citizenship_id = this.country_of_birth_id
      }
      if (this.documentIssuedCountryAsResidence) {
        this.document_issued_country_id = this.country_of_residence_id
        this.document_issued_location_id = this.city_of_residence_id
      }
    },
    handleTypologyChange() {
      this.setDirtiness('typology')
      if (this.roomChiefTypologies.filter(t => t.value === this.typology)) {
        this.$emit('typology:room-chief-changed', {
          guestId: this.guest.id,
          typology: this.typology,
        })
      }
    },
    // isExpectedByGuestTypology(fieldName) {
    //   return (this.isRoomChiefOrSingle && CHIEF_GUEST_FIELDS.includes(fieldName)) ||  MEMBER_GUEST_FIELDS.includes(fieldName)
    // },
    getCurrentValues() {
      const values = {}
      FORM_FIELDS.forEach(field => (values[field] = this[field]))
      // Quando si forzava il dato in questo punto, senza toccare le variabili "data", si trovava il valore uguale a
      // quello del campo copiato. Es: Nazione nascita "Italia", uncheck "Dati residenza come nascita", -> si vedeva "Italia"
      // if (this.countryOfResidenceAsBirth) {
      //   values['country_of_residence_id'] = this.country_of_birth_id
      //   values['city_of_residence_id'] = this.place_of_birth_id
      // }
      // if (this.countryOfCitizenshipAsBirth) {
      //   values['citizenship_id'] = this.country_of_birth_id
      // }
      // console.log('getCurrentValues', values)
      return values
    },
//    undoChanges() {
//      Object.entries(this.initialValues).forEach(([key, value]) => {
//        this[key] = value
//      })
//      this.$v.$reset()
//    },
    setDirtiness(fieldName) {
      this[fieldName] != this.initialValues[fieldName]
        ? this.$v[fieldName].$touch()
        : this.$v[fieldName].$reset()
    },
    handleCountryOfResidenceAsBirth(value) {
      if (value) {
        this.country_of_residence_id = this.country_of_birth_id
        this.city_of_residence_id = this.place_of_birth_id
      }
      this.setDirtiness('country_of_residence_id')
      this.setDirtiness('city_of_residence_id')
    },
    handleCountryOfCitizenshipAsBirth(value) {
      if (value) {
        this.citizenship_id = this.country_of_birth_id
      } else {
        this.citizenship_id = null
      }
      this.setDirtiness('citizenship_id')
      if (this.citizenship_id) {
        this.loadIdentificationDocuments(this.citizenship_id)
      } else {
        this.$store.dispatch('mappings/clearIdentificationDocuments')
      }
    },
    handleDocumentsCountriesChanged(value) {
      if (value) this.loadIdentificationDocuments(value)
    },
    handleDocumentIssuedCountryAsResidence(value) {
      if (value) {
        this.document_issued_country_id = this.country_of_residence_id
        this.document_issued_location_id = this.city_of_residence_id
      } else {
        this.document_issued_country_id = null
        this.document_issued_location_id = null
      }
      this.setDirtiness('document_issued_country_id')
      this.setDirtiness('document_issued_location_id')

      if (value) {
        this.document_issued_location_name = ''
        this.document_issued_location_id = null
        this.setDirtiness('document_issued_location_id')
      }
    },
    handleUseRoomChiefContacts(value) {
      if (value) {
        this.email = null
        this.setDirtiness('email')
        this.email_confirmation = null
        this.setDirtiness('email_confirmation')
        this.phone_prefix = null
        this.setDirtiness('phone_prefix')
        this.phone = null
        this.setDirtiness('phone')
      }
    },
    copyDataFromCurrentCustomer(customer) {
      this.first_name = customer.first_name
      this.last_name = customer.last_name
      this.sex = customer.sex
      this.fiscal_code = customer.fiscal_code
      this.email = customer.email
      this.email_confirmation = customer.email
      this.phone = customer.phone
      this.phone_prefix = customer.phone_prefix
    },
    loadIdentificationDocuments(citizenship_id) {
      //console.log('loadIdentificationDocuments', citizenship_id)
      const countries = this.countryOfCitizenshipAsBirth ? this.birthCountries : this.citizenshipCountries
      const citizenship = countries.find(country => country.id == citizenship_id)
      if (citizenship) {
        // EU members + Iceland, Norway, Switzerland, Liechtenstein
        const schengenArea = (citizenship.ue || citizenship.iso2 === 'IS' || citizenship.iso2 === 'NO' || citizenship.iso2 === 'CH' || citizenship.iso2 === 'LI')
        this.$store.dispatch('mappings/fetchIdentificationDocuments', {isUe: schengenArea })
      } else {
        this.$store.dispatch('mappings/fetchIdentificationDocuments', {})
      }
    },
    clearFields(fields, newValue=null) {
      fields.forEach(field => {
        this[field] = newValue
      })
    },
    // Non dovrebbe più servire in quanto i form vengono ricreati completamente
    // Valutare il riutilizzo quando si ottimizzerà il form
    // refreshGuest() {
    //   Object.assign(this.$data, this.$options.data.apply(this))
    //   this.initialValues = this.getCurrentValues()
    // },
  },
  watch: {
    '$i18n.locale':  function(newVal, oldVal){
        this.birthCountries = []
        this.birthPlaces = []
        this.residenceCountries = []
        this.documentCountries = []
        this.residenceCities = []
        this.documentIssuedLocationCities = []
        this.citizenshipCountries = []
    },
    '$v.$anyDirty': function(newVal, oldVal) {
      //console.log('guest_form watch $v.$anyDirty')
      this.$emit('form:dirty', newVal)
    },

    '$v.$invalid': function(newVal, oldVal) {
      //console.log('guest_form watch $v.$invalid')
      this.$emit('form:invalid', newVal)
    },

    birthdatePickerOpened(val) {
      val && setTimeout(() => (this.$refs.birthday_picker.activePicker = 'YEAR'))
    },
    documentIssuedOnPickerOpened(val) {
      val && setTimeout(() => (this.$refs.document_issued_on_picker.activePicker = 'YEAR'))
    },
    documentExpiringDatePickerOpened(val) {
      val && setTimeout(() => (this.$refs.document_expiring_date_picker.activePicker = 'YEAR'))
    },

    country_of_birth_name: {
      immediate: true,
      handler: function(searchName) {
        if (!searchName) return
        //console.log('Fetch birth countries start with', searchName)

        // Items have already been requested
        if (this.isLoading.birthCountries) return

        this.isLoading.birthCountries = true

        this.$store
          .dispatch('mappings/fetchCountries', { name: searchName })
          .then(_ => {
            //console.log('fetch birth countries ok', this.countries.length)
            this.birthCountries = this.countries
            if (this.countryOfCitizenshipAsBirth && this.country_of_birth_id) this.loadIdentificationDocuments(this.country_of_birth_id)
            this.handleCountryOfResidenceAsBirth(this.countryOfResidenceAsBirth)
          })
          .catch(err => {
            console.log(err)
            this.$store.dispatch('layout/replaceAlert', {
              type: 'error',
              message: err,
            })
          })
          .finally(() => (this.isLoading.birthCountries = false))
      },
    },

    place_of_birth_name: {
      immediate: true,
      handler: function(searchName) {
        if (!searchName) return
        //console.log('Fetch cities of birth start with', searchName)

        // Items have already been requested
        if (this.isLoading.birthPlaces) return

        this.isLoading.birthPlaces = true

        this.$store
          .dispatch('mappings/fetchCities', { name: searchName, country_id: this.country_of_birth_id })
          .then(_ => {
            //console.log('fetch cities of birth ok', this.cities.length)
            this.birthPlaces = this.cities
          })
          .catch(err => {
            console.log(err)
            this.$store.dispatch('layout/replaceAlert', {
              type: 'error',
              message: err,
            })
          })
          .finally(() => (this.isLoading.birthPlaces = false))
      },
    },

    country_of_residence_name: {
      immediate: true,
      handler: function(searchName) {
        if (!searchName) return
        //console.log('Fetch residence countries start with', searchName)

        // Items have already been requested
        if (this.isLoading.residenceCountries) return

        this.isLoading.residenceCountries = true

        this.$store
          .dispatch('mappings/fetchCountries', { name: searchName })
          .then(_ => {
            //console.log('fetch residence countries ok', this.countries.length)
            this.residenceCountries = this.countries
          })
          .catch(err => {
            console.log(err)
            this.$store.dispatch('layout/replaceAlert', {
              type: 'error',
              message: err,
            })
          })
          .finally(() => (this.isLoading.residenceCountries = false))
      },
    },

    city_of_residence_name: {
      immediate: true,
      handler: function(searchName) {
        if (!searchName) return
        //console.log('Fetch cities of residence start with', searchName)

        // Items have already been requested
        if (this.isLoading.residenceCities) return

        this.isLoading.residenceCities = true

        this.$store
          .dispatch('mappings/fetchCities', { name: searchName, country_id: this.country_of_residence_id })
          .then(_ => {
            //console.log('fetch cities of residence ok', this.cities.length)
            this.residenceCities = this.cities
          })
          .catch(err => {
            console.log(err)
            this.$store.dispatch('layout/replaceAlert', {
              type: 'error',
              message: err,
            })
          })
          .finally(() => (this.isLoading.residenceCities = false))
      },
    },

    citizenship_name: {
      immediate: true,
      handler: function(searchName) {
        if (!searchName) return
        //console.log('Fetch citizenships start with', searchName)

        // Items have already been requested
        if (this.isLoading.citizenshipCountries) return

        this.isLoading.citizenshipCountries = true

        this.$store
          .dispatch('mappings/fetchCountries', { name: searchName })
          .then(_ => {
            //console.log('fetch citizenships ok', this.countries.length)
            this.citizenshipCountries = this.countries
            if (this.citizenship_id) this.loadIdentificationDocuments(this.citizenship_id)
          })
          .catch(err => {
            console.log(err)
            this.$store.dispatch('layout/replaceAlert', {
              type: 'error',
              message: err,
            })
          })
          .finally(() => (this.isLoading.citizenshipCountries = false))
      },
    },

    // documentCountries
    document_issued_country_name: {
      immediate: true,
      handler: function(searchName) {
        if (!searchName) return
        //console.log('Fetch document countries start with', searchName)

        // Items have already been requested
        if (this.isLoading.documentCountries) return

        this.isLoading.documentCountries = true

        this.$store
          .dispatch('mappings/fetchCountries', { name: searchName })
          .then(_ => {
            //console.log('fetch document countries ok', this.countries.length)
            this.documentCountries = this.countries
          })
          .catch(err => {
            console.log(err)
            this.$store.dispatch('layout/replaceAlert', {
              type: 'error',
              message: err,
            })
          })
          .finally(() => (this.isLoading.documentCountries = false))
      },
    },

    document_issued_location_name: {
      immediate: true,
      handler: function(searchName) {
        if (!searchName) return
        //console.log('Fetch document issued locations start with', searchName)

        // Items have already been requested
        if (this.isLoading.documentIssuedLocationCities) return

        this.isLoading.documentIssuedLocationCities = true

        this.$store
          .dispatch('mappings/fetchCities', { name: searchName, country_id: this.document_issued_country_id })
          .then(_ => {
            //console.log('fetch document issued locations ok', this.cities.length)
            this.documentIssuedLocationCities = this.cities
          })
          .catch(err => {
            console.log(err)
            this.$store.dispatch('layout/replaceAlert', {
              type: 'error',
              message: err,
            })
          })
          .finally(() => (this.isLoading.documentIssuedLocationCities = false))
      },
    },

    phonePrefixSearch: {
      immediate: true,
      handler: function(search) {
        if (!search) return
        //console.log('Fetch PhonePrefixes start with', search)

        // Items have already been requested
        if (this.isLoading.phonePrefixes) return
        this.isLoading.phonePrefixes = true

        this.$store
          .dispatch('mappings/fetchPhonePrefixes', { name: search })
          .then(_ => {
            //console.log('fetchPhonePrefixes ok', this.phonePrefixes.length)
          })
          .catch(err => {
            console.log(err)
            this.$store.dispatch('layout/replaceAlert', {
              type: 'error',
              message: err,
            })
          })
          .finally(() => (this.isLoading.phonePrefixes = false))
      },
    },
  },
}
</script>

<style lang='scss' scoped>
  .slide-fade-enter {
    transform: translateY(-15px);
    opacity: 0;
  }
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.2s ease;
  }
  .slide-fade-leave-to {
    transform: translateY(-15px);
    opacity: 0;
  }
</style>
